import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useActiveProfile } from './ActiveProfileContext'
import { useAuth } from './AuthContext'
import { useFeatureAccess } from '../hooks/useFeatureAccess'

interface ClientViewContextType {
  /** Whether Pro user is currently viewing a client's data */
  isViewingClient: boolean
  /** The client profile being viewed (if any) */
  viewedClientProfile: {
    profileId: string
    name: string
    ownerUid: string
  } | null
  /** Enter client viewing mode */
  enterClientView: (profileId: string, name: string, ownerUid: string) => void
  /** Exit client viewing mode and return to Pro dashboard */
  exitClientView: () => void
}

const ClientViewContext = createContext<ClientViewContextType | undefined>(undefined)

interface ClientViewProviderProps {
  children: ReactNode
}

const SESSION_KEY = 'pro_client_view'

export const ClientViewProvider: React.FC<ClientViewProviderProps> = ({ children }) => {
  const { currentUser } = useAuth()
  const { activeProfile } = useActiveProfile()
  const { plan, features } = useFeatureAccess()
  
  // Check if user has Pro access
  const hasProAccess = plan === 'pro' || features.includes('client_management')
  
  const [viewedClientProfile, setViewedClientProfile] = useState<{
    profileId: string
    name: string
    ownerUid: string
  } | null>(null)

  // Restore client view state from session storage on mount
  useEffect(() => {
    if (!hasProAccess) return
    
    try {
      const stored = sessionStorage.getItem(SESSION_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        setViewedClientProfile(parsed)
      }
    } catch {
      // Ignore parsing errors
    }
  }, [hasProAccess])

  // Sync active profile changes with client view state
  useEffect(() => {
    if (!hasProAccess || !currentUser?.uid || !activeProfile) return
    
    // If active profile is owned by another user, we're viewing a client
    if (activeProfile.ownerUid && activeProfile.ownerUid !== currentUser.uid) {
      const clientData = {
        profileId: activeProfile.profileId,
        name: activeProfile.name || 'Client',
        ownerUid: activeProfile.ownerUid
      }
      setViewedClientProfile(clientData)
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(clientData))
    } else if (viewedClientProfile) {
      // User switched back to their own profile - exit client view
      setViewedClientProfile(null)
      sessionStorage.removeItem(SESSION_KEY)
    }
  }, [activeProfile, currentUser?.uid, hasProAccess])

  const enterClientView = useCallback((profileId: string, name: string, ownerUid: string) => {
    const clientData = { profileId, name, ownerUid }
    setViewedClientProfile(clientData)
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(clientData))
  }, [])

  const exitClientView = useCallback(() => {
    setViewedClientProfile(null)
    sessionStorage.removeItem(SESSION_KEY)
  }, [])

  const isViewingClient = useMemo(() => {
    return hasProAccess && viewedClientProfile !== null
  }, [hasProAccess, viewedClientProfile])

  const value: ClientViewContextType = useMemo(() => ({
    isViewingClient,
    viewedClientProfile,
    enterClientView,
    exitClientView
  }), [isViewingClient, viewedClientProfile, enterClientView, exitClientView])

  return (
    <ClientViewContext.Provider value={value}>
      {children}
    </ClientViewContext.Provider>
  )
}

export const useClientView = (): ClientViewContextType => {
  const context = useContext(ClientViewContext)
  if (context === undefined) {
    throw new Error('useClientView must be used within a ClientViewProvider')
  }
  return context
}


















